import type { ForwardedRef, ReactNode } from 'react';

import { forwardRef, memo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { isType, omit } from '@ricoh/ria.client.core/utils/data/object';
import classNames from 'classnames';

import type { AppIconsProps, FontawesomeProps, GoogleIconProps, IconProps, IconRef, OctIconProps } from './_.type';

import useIconHooks from './_.hook';

/** Icon. */
export default memo(
  forwardRef(function Icon(props: IconProps, ref: ForwardedRef<IconRef>): ReactNode {
    const { rotate, className } = props;

    const { states } = useIconHooks(props, ref);

    return (
      <span
        className={classNames('d-inline-block lh-1 align-middle user-select-none pointer-event-none align-self-center', className)}
        style={{
          transform: `rotate(${rotate}deg)`,
        }}
      >
        {(() => {
          if (isType<AppIconsProps>(states.iconRenderingProps, 'app')) {
            const { icon: AppIcon } = states.iconRenderingProps;

            return <AppIcon />;
          }

          if (isType<FontawesomeProps>(states.iconRenderingProps, 'fontawesome')) {
            return <FontAwesomeIcon {...omit(states.iconRenderingProps, '$is')} />;
          }

          if (isType<OctIconProps>(states.iconRenderingProps, 'oct')) {
            const { icon: Octicon, ...iconProps } = states.iconRenderingProps;

            return <Octicon {...omit(iconProps, '$is')} />;
          }

          if (isType<GoogleIconProps>(states.iconRenderingProps, 'google')) {
            const { name, style } = states.iconRenderingProps;

            return (
              <span
                className="material-symbols-outlined align-top"
                style={style}
              >
                {name}
              </span>
            );
          }

          return null;
        })()}
      </span>
    );
  }),
);
